import React, {
  ComponentProps,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';

import {
  Button,
  ListOption,
  Tabs,
  TextField,
  Typography,
} from '@zendesk/forethought-elements';
import { SelectDropdown } from '@zendesk/forethought-elements';
import {
  PROACTIVE_CONVERSATIONS_SYSTEM_CONTEXT_VARIABLES,
  systemContextVariablesOptions,
  systemContextVariablesOptionsWithoutUsableSystemContextVariables,
} from '../dashboard-pages/solve-insights/cards/systemContextVariablesOptions';
import { useIsInReadOnlyScope } from 'src/components/read-only-scope/readOnlyScope';
import { useCanEditContextVariables } from 'src/hooks/useChannelAccess';
import { useGetContextVariables } from 'src/hooks/useGetContextVariables';
import {
  ALLOWED_DISPLAY_NAME_CHARACTER_REGEX,
  ALLOWED_DISPLAY_NAME_LENGTH,
} from 'src/pages/action-builder/context-variables-settings/constants';
import useIsProactiveConversationsEnabled from 'src/pages/intent-conversation-analytics/hooks/useIsProactiveConversationsEnabled';
import { useCreateContextVariableMutation } from 'src/services/action-builder/actionBuilderApi';
import { SystemContextVariableType } from 'src/services/insight-cards/types';
import { ContextVariable } from 'src/types/actionBuilderApiTypes';
import { ContextVariableTypes } from 'src/utils/enums';
import { flattenDynamicContextVariables } from 'src/utils/solve/dynamicContextVariableUtils';

interface ContextVariableListOption extends ListOption {
  value: keyof typeof ContextVariableTypes;
}

type DefaultValueType = 'context_variable' | 'system_context_variable';

const defaultAdditionalOptions: (ListOption & { category: string })[] = [];
const defaultDisabledContextVariableTypes: ContextVariable['context_variable_type'][] =
  [];
const defaultUndefinedContextVariables: string[] = [];

interface BaseContextVariableSelectDropdownProps<
  ExtraValueType extends string = string,
> {
  additionalOptions?: (ListOption & { category: string })[];
  'aria-label'?: string;
  contextVariableTypeOptions?: ContextVariableListOption[];
  disabled?: boolean;
  disabledContextVariableTypes?: ContextVariable['context_variable_type'][];
  error?: string | boolean;
  extraTabsDefinitions?: {
    addNewButtonText?: string;
    onAddNewClick?: () => void;
    options: ListOption[];
    tabName: string;
    valueType: ExtraValueType;
  }[];
  filterFn?: (cv: ContextVariable) => boolean;
  fullWidth?: boolean;
  id?: string;
  isClearable?: boolean;
  label?: string;
  onClear?: () => void;
  placeholder?: string;
  shouldErrorMessageTakeUpSpace?: boolean;
  shouldIncludeCustomMetricsSystemContextVariables?: boolean;
  shouldIncludeDynamicListContextVariableChildren?: boolean;
  shouldIncludeDynamicListContextVariables?: boolean;
  shouldIncludeRuntimeSystemContextVariables?: boolean;
  shouldIncludeSystemContextVariables?: boolean;
  shouldIncludeTemplateContextVariables?: boolean;
  shouldIncludeWorkflowTagContextVariable?: boolean;
  shouldOnlyReturnSupportedFilterableContextVariables?: boolean;
  shouldOverrideDisabledContextVariableSelection?: boolean;
  shouldProvideCVIdFormatting?: boolean;
  showRequiredLabel?: boolean;
  undefinedContextVariables?: string[];
  valueType?: DefaultValueType | ExtraValueType;
}

interface SingleSelectProps<
  ExtraValueType extends string = string,
> extends BaseContextVariableSelectDropdownProps<ExtraValueType> {
  multiple?: false;
  onChange: (
    newValue: string,
    valueType?: DefaultValueType | ExtraValueType,
  ) => void;
  value?: string;
}

interface MultiSelectProps<
  ExtraValueType extends string = string,
> extends BaseContextVariableSelectDropdownProps<ExtraValueType> {
  multiple: true;
  onChange: (
    newValue: string | string[],
    valueType?: DefaultValueType | ExtraValueType,
  ) => void;
  value?: string | string[];
}

type ContextVariableSelectDropdownProps<
  ExtraValueType extends string = string,
> = SingleSelectProps<ExtraValueType> | MultiSelectProps<ExtraValueType>;

const ContextVariableSelectDropdown = <ExtraValueType extends string>({
  additionalOptions = defaultAdditionalOptions,
  contextVariableTypeOptions,
  disabled: disabledProp = false,
  disabledContextVariableTypes = defaultDisabledContextVariableTypes,
  error = false,
  extraTabsDefinitions,
  filterFn,
  fullWidth = false,
  id = 'context-variables-dropdown',
  label,
  multiple = false,
  onChange,
  placeholder = 'Select Context Variable',
  shouldErrorMessageTakeUpSpace,
  shouldIncludeCustomMetricsSystemContextVariables = false,
  shouldIncludeDynamicListContextVariableChildren = true,
  shouldIncludeDynamicListContextVariables = true,
  shouldIncludeRuntimeSystemContextVariables = false,
  shouldIncludeSystemContextVariables = false,
  shouldIncludeTemplateContextVariables = false,
  shouldIncludeWorkflowTagContextVariable = true,
  shouldOnlyReturnSupportedFilterableContextVariables = false,
  shouldOverrideDisabledContextVariableSelection = false,
  shouldProvideCVIdFormatting = false,
  showRequiredLabel = false,
  undefinedContextVariables = defaultUndefinedContextVariables,
  value = '',
  valueType = 'context_variable',
  ...props
}: ContextVariableSelectDropdownProps<ExtraValueType>) => {
  const isInReadOnlyScope = useIsInReadOnlyScope();
  const disabled = disabledProp || isInReadOnlyScope;
  const { t } = useTranslation('workflow-builder-edit');
  const { palette } = useTheme();
  const isProactiveConversationsEnabled = useIsProactiveConversationsEnabled();
  const singleSelectValue = useMemo(
    () => (typeof value === 'string' ? value : ''),
    [value],
  );
  const multiSelectValue = useMemo(
    () => (Array.isArray(value) ? value : []),
    [value],
  );
  const [createContextVariable] = useCreateContextVariableMutation();
  const canEditContextVariables = useCanEditContextVariables();
  const { contextVariables } = useGetContextVariables({
    filterFn,
    shouldIncludeDynamicListContextVariables,
    shouldIncludeRuntimeSystemContextVariables,
    shouldIncludeSystemContextVariables,
    shouldIncludeTemplateContextVariables,
    shouldIncludeWorkflowTagContextVariable,
    shouldOnlyReturnSupportedFilterableContextVariables,
  });

  const a11yProps = useCallback(
    (index: number) => {
      return {
        'aria-controls': `tabpanel-${index}`,
        id: `${id}-tab-${index}`,
      };
    },
    [id],
  );

  const tabOptions = useMemo(
    () => [
      ...(extraTabsDefinitions?.map(tab => tab.tabName) ?? []),
      'Context Variables',
    ],
    [extraTabsDefinitions],
  );

  const flattenedContextVariables = useMemo(() => {
    return flattenDynamicContextVariables(
      contextVariables,
      shouldIncludeDynamicListContextVariableChildren,
    );
  }, [contextVariables, shouldIncludeDynamicListContextVariableChildren]);

  const [isCreateMode, setIsCreateMode] = useState(false);

  const handleReset = useCallback(() => {
    setIsCreateMode(false);
  }, []);

  const hasSelectedValue = multiple
    ? multiSelectValue.length > 0
    : singleSelectValue !== '';

  const initialTabIndex =
    // If we have a specific value selected, go to the appropriate tab
    hasSelectedValue && valueType
      ? valueType === 'context_variable' ||
        valueType === 'system_context_variable'
        ? (extraTabsDefinitions?.length ?? 0)
        : (extraTabsDefinitions?.findIndex(
            tab => tab.valueType === valueType,
          ) ?? 0)
      : // Otherwise, default to Events tab (index 0) if available, or Context Variables
        extraTabsDefinitions?.length
        ? 0
        : 0;

  const [tabIndex, setTabIndex] = useState<number>(initialTabIndex);

  const extraTabIndex = tabIndex;

  const currentExtraTabDefinition =
    extraTabsDefinitions && tabIndex < extraTabsDefinitions.length
      ? extraTabsDefinitions[extraTabIndex]
      : undefined;

  const newValueType =
    currentExtraTabDefinition?.valueType || 'context_variable';

  const undefinedCvSet = useMemo(
    () => new Set(undefinedContextVariables),
    [undefinedContextVariables],
  );

  const labelToRender =
    !label && props['aria-label'] ? '' : (label ?? 'Context Variable');

  const normalizedOnChange = useCallback(
    (
      newValue: string | string[],
      valueType?: DefaultValueType | ExtraValueType,
    ) => {
      if (multiple) {
        (
          onChange as (
            newValue: string | string[],
            valueType?: DefaultValueType | ExtraValueType,
          ) => void
        )(newValue, valueType);
      } else {
        const stringValue = Array.isArray(newValue)
          ? newValue[0] || ''
          : newValue;
        (
          onChange as (
            newValue: string,
            valueType?: DefaultValueType | ExtraValueType,
          ) => void
        )(stringValue, valueType);
      }
    },
    [multiple, onChange],
  );
  const onSave = useCallback(
    async (cvName: string, cvType: keyof typeof ContextVariableTypes) => {
      const res = await createContextVariable({
        context_variable_name: cvName,
        context_variable_type: cvType,
      });

      if (!('data' in res) || !res.data) {
        console.error('Cv update failed');
        return;
      }

      if (multiple) {
        normalizedOnChange(
          [...multiSelectValue, res.data.context_variable_id],
          newValueType,
        );
      } else {
        normalizedOnChange(res.data.context_variable_id, newValueType);
      }
      handleReset();
    },
    [
      createContextVariable,
      multiple,
      handleReset,
      normalizedOnChange,
      multiSelectValue,
      newValueType,
    ],
  );

  const customMetricsSystemContextVariables = useMemo(
    () =>
      systemContextVariablesOptionsWithoutUsableSystemContextVariables
        // Proactive variables are only offered to entitled orgs.
        .filter(
          option =>
            isProactiveConversationsEnabled ||
            !PROACTIVE_CONVERSATIONS_SYSTEM_CONTEXT_VARIABLES.includes(
              option.value,
            ),
        )
        .map(
          option =>
            ({
              ...option,
              category: 'System Context Variables',
              color: palette.colors.blue['500'],
            }) as const satisfies ListOption & {
              value: SystemContextVariableType;
            },
        ),
    [palette.colors.blue, isProactiveConversationsEnabled],
  );

  const tabs = useMemo(
    () =>
      extraTabsDefinitions && (
        <Tabs
          a11yProps={a11yProps}
          aria-label={t('contextVariableSelectDropdown.tabsAriaLabel')}
          onChange={(_, newValue) => setTabIndex(newValue)}
          tabs={tabOptions}
          typographyVariant='font14Bold'
          value={tabIndex}
          variant='fullWidth'
        />
      ),
    [a11yProps, extraTabsDefinitions, t, tabIndex, tabOptions],
  );

  /**
   * `<div>` IS REQUIRED HERE. Don't remove it or change it to fragment.
   *
   * I spent an hour trying to fix this warning:
   *
   * > MUI: The `value` provided to the Tabs component is invalid.
   * > None of the Tabs' children match with "undefined".
   * > You can provide one of the following values: 0, 1.
   *
   * And I still have no idea why it happens.
   */
  const wrappedTabs = useMemo(() => tabs && <div>{tabs}</div>, [tabs]);

  const topMenuChildren = useMemo(
    () =>
      currentExtraTabDefinition ? (
        wrappedTabs
      ) : isCreateMode ? (
        <div>
          {tabs}
          <ContextVariableMenuForm
            contextVariableTypeOptions={contextVariableTypeOptions}
            onClose={handleReset}
            onSave={onSave}
          />
        </div>
      ) : (
        wrappedTabs
      ),
    [
      contextVariableTypeOptions,
      currentExtraTabDefinition,
      handleReset,
      isCreateMode,
      onSave,
      tabs,
      wrappedTabs,
    ],
  );

  const extraTabOptions = currentExtraTabDefinition?.options;

  const options = useMemo(
    () =>
      extraTabOptions || [
        ...(shouldIncludeCustomMetricsSystemContextVariables
          ? customMetricsSystemContextVariables
          : []),
        ...additionalOptions,
        ...flattenedContextVariables.map(
          ({ disabled, fullLabel, id, indent, label, type }) => ({
            category:
              additionalOptions.length ||
              shouldIncludeCustomMetricsSystemContextVariables
                ? 'Context Variables'
                : undefined,
            color: undefinedCvSet.has(id)
              ? palette.error.main
              : palette.primary.main,
            disabled:
              (!shouldOverrideDisabledContextVariableSelection &&
                disabled &&
                shouldIncludeDynamicListContextVariableChildren) ||
              disabledContextVariableTypes?.includes(type),
            indent,
            label: '$' + (fullLabel ?? label),
            value: shouldProvideCVIdFormatting ? `{{${id}}}` : id,
          }),
        ),
      ],
    [
      additionalOptions,
      customMetricsSystemContextVariables,
      disabledContextVariableTypes,
      extraTabOptions,
      flattenedContextVariables,
      palette.error.main,
      palette.primary.main,
      shouldIncludeCustomMetricsSystemContextVariables,
      shouldIncludeDynamicListContextVariableChildren,
      shouldOverrideDisabledContextVariableSelection,
      shouldProvideCVIdFormatting,
      undefinedCvSet,
    ],
  );

  const handleClear = useMemo(
    () =>
      props.onClear ||
      (props.isClearable
        ? () => {
            normalizedOnChange(multiple ? [] : '', newValueType);
            handleReset();
          }
        : undefined),
    [
      props.onClear,
      props.isClearable,
      normalizedOnChange,
      multiple,
      newValueType,
      handleReset,
    ],
  );

  const handleChange: ComponentProps<
    typeof SelectDropdown<string | string[]>
  >['onChange'] = useCallback(
    e => {
      if (e?.target.value === undefined) {
        handleReset();
        return;
      }

      if (multiple) {
        const nextValues = Array.isArray(e.target.value)
          ? e.target.value
          : [e.target.value];
        normalizedOnChange(nextValues, newValueType);
        return;
      }

      const nextValue = (e?.target.value ?? '') as string;
      const derivedValueType =
        newValueType === 'context_variable' &&
        systemContextVariablesOptions
          .map(cv => cv.value)
          .includes(nextValue as SystemContextVariableType)
          ? 'system_context_variable'
          : newValueType;

      normalizedOnChange(nextValue, derivedValueType);
      handleReset();
    },
    [handleReset, multiple, newValueType, normalizedOnChange],
  );

  const extraTabOnAddNewClick = currentExtraTabDefinition?.onAddNewClick;

  const handleAddNewClick = useMemo(
    () =>
      // CV-create is exclusive to the CV tab; extra tabs only get their own action.
      !canEditContextVariables || currentExtraTabDefinition
        ? currentExtraTabDefinition
          ? extraTabOnAddNewClick
          : undefined
        : isCreateMode || additionalOptions.length
          ? undefined
          : (e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              setIsCreateMode(true);
            },
    [
      additionalOptions.length,
      canEditContextVariables,
      currentExtraTabDefinition,
      extraTabOnAddNewClick,
      isCreateMode,
    ],
  );

  const handleClose = useCallback(() => {
    setTabIndex(initialTabIndex);
    setIsCreateMode(false);
  }, [initialTabIndex]);

  // It has to be `useLayoutEffect`, not `useEffect` to avoid flickering:
  useLayoutEffect(() => {
    setTabIndex(initialTabIndex);
  }, [initialTabIndex]);

  const extraTabAddNewButtonText = currentExtraTabDefinition?.addNewButtonText;

  const addNewButtonText = currentExtraTabDefinition
    ? (extraTabAddNewButtonText ?? '')
    : isCreateMode
      ? ''
      : 'Create new Context Variable';

  /**
   * Avoid warning:
   * You have provided an out-of-range value `...` for the select component.
   */
  const normalizedValue = useMemo(() => {
    const optionValues = options.map(option => option.value);

    if (multiple) {
      return multiSelectValue.filter(selectedValue =>
        optionValues.includes(selectedValue),
      );
    }

    return optionValues.includes(singleSelectValue) ? singleSelectValue : '';
  }, [multiSelectValue, multiple, options, singleSelectValue]);

  if (!disabled && shouldIncludeTemplateContextVariables) {
    throw new Error('Cannot use template CV in live input');
  }

  return (
    <Box
      sx={{
        backgroundColor: palette.colors.white,
        width: fullWidth ? '100%' : undefined,
      }}
    >
      <SelectDropdown<string | string[]>
        addNewButtonText={addNewButtonText}
        aria-label={props['aria-label']}
        disabled={disabled}
        error={error}
        id={id}
        isMenuSearchable
        key={contextVariables.length}
        label={labelToRender}
        menuMaxHeight={450}
        multiple={multiple}
        onAddNewClick={handleAddNewClick}
        onChange={handleChange}
        onClear={handleClear}
        onClose={handleClose}
        options={options}
        placeholder={placeholder}
        shouldErrorMessageTakeUpSpace={shouldErrorMessageTakeUpSpace}
        showRequiredLabel={showRequiredLabel}
        topMenuChildren={topMenuChildren}
        value={normalizedValue}
      />
    </Box>
  );
};

interface ContextVariableMenuFormProps {
  contextVariableTypeOptions?: ContextVariableListOption[];
  onClose: () => void;
  onSave: (
    newCvName: string,
    newCvType: keyof typeof ContextVariableTypes,
  ) => void;
}

const getContextVariableTypesOptions = () => {
  return Object.keys(ContextVariableTypes)
    .map(type => ({
      disabled:
        type === 'DYNAMIC_LIST' ||
        type === 'DYNAMIC_LIST_MULTI_SELECT' ||
        type === 'MULTIOPTIONS' ||
        type === 'LIST' ||
        type === 'MULTI_SELECT_LIST',
      label: ContextVariableTypes[type as keyof typeof ContextVariableTypes],
      value: type,
    }))
    .filter(type => type.label !== ContextVariableTypes.MULTIOPTIONS);
};
export const ContextVariableMenuForm = ({
  contextVariableTypeOptions,
  onClose,
  onSave,
}: ContextVariableMenuFormProps) => {
  const { palette } = useTheme();
  const { t } = useTranslation('workflow-builder-edit');

  const [newCvName, setNewCvName] = useState('');
  const [newCvType, setNewCvType] = useState<keyof typeof ContextVariableTypes>(
    contextVariableTypeOptions?.[0]?.value ?? 'SHORT_TEXT',
  );

  const { contextVariables } = useGetContextVariables();

  const getErrorMessage = () => {
    if (newCvName.length > ALLOWED_DISPLAY_NAME_LENGTH) {
      return `Display names must be ${ALLOWED_DISPLAY_NAME_LENGTH} characters or less`;
    } else if (ALLOWED_DISPLAY_NAME_CHARACTER_REGEX.test(newCvName)) {
      return 'Display names must not use special characters';
    } else if (
      contextVariables.some(
        cv =>
          cv.context_variable_name.trim().toLowerCase() ===
          newCvName.trim().toLowerCase(),
      )
    ) {
      return 'Display names must be unique';
    } else if (newCvName === '') {
      return 'Display names must be greater than 1 character';
    }
    return false;
  };

  const hasDisplayNameErrors = getErrorMessage();

  const clearForm = () => {
    setNewCvName('');
    setNewCvType('SHORT_TEXT');
  };

  const handleSave = () => {
    onSave(newCvName, newCvType);
    clearForm();
    onClose();
  };

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${palette.colors.slate[200]}`,
        borderTop: `1px solid ${palette.colors.slate[200]}`,
        padding: '20px 16px 18px 16px',
      }}
    >
      <Box
        sx={{
          pb: '17px',
        }}
      >
        <Typography variant='font14Bold'>{t('cvMenuForm.title')}</Typography>
      </Box>
      <Box
        sx={{
          pb: '12px',
        }}
      >
        <TextField
          aria-label={t('cvMenuForm.nameAriaLabel')}
          error={hasDisplayNameErrors}
          id='cv-name-field'
          onChange={e => {
            const { value } = e.target;
            setNewCvName(value);
          }}
          placeholder={t('cvMenuForm.namePlaceholder')}
          value={newCvName}
        />
      </Box>
      <Box
        sx={{
          pb: '16px',
        }}
      >
        <SelectDropdown
          id={'cv-type-selector'}
          onChange={e => {
            const { value } = e.target;
            setNewCvType(value as keyof typeof ContextVariableTypes);
          }}
          options={
            contextVariableTypeOptions ?? getContextVariableTypesOptions()
          }
          placeholder={t('cvMenuForm.typePlaceholder')}
          value={newCvType}
        />
      </Box>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: '10px',
        }}
      >
        <Button
          id='cv-cancel-create'
          onClick={() => {
            clearForm();
            onClose();
          }}
          variant='secondary'
        >
          {t('cvMenuForm.cancel')}
        </Button>
        <Button
          disabled={Boolean(hasDisplayNameErrors)}
          id='cv-save'
          onClick={handleSave}
          variant='main'
        >
          {t('cvMenuForm.save')}
        </Button>
      </Box>
    </Box>
  );
};

export default ContextVariableSelectDropdown;
